/**
 * Modelo FormaPago - Formas de Pago
 * Separado en sub-interfaces según el tipo de pago,
 * ya que no todos los tipos requieren los mismos campos.
 */

// Enum con los tipos de pago disponibles
export enum PaymentMethodType {
  Efectivo = 0,
  Transferencia = 1,
  TarjetaDebito = 2,
  TarjetaCredito = 3,
  Cheque = 4,
  BilleteraVirtual = 5,
  Pix = 6,
  BoletoBancario = 7,
  CartaoCredito = 8,
  Otro = 9,
}

// Labels para mostrar en UI
export const PaymentMethodTypeLabels: Record<PaymentMethodType, string> = {
  [PaymentMethodType.Efectivo]: 'Efectivo',
  [PaymentMethodType.Transferencia]: 'Transferencia',
  [PaymentMethodType.TarjetaDebito]: 'Tarjeta Débito',
  [PaymentMethodType.TarjetaCredito]: 'Tarjeta Crédito',
  [PaymentMethodType.Cheque]: 'Cheque',
  [PaymentMethodType.BilleteraVirtual]: 'Billetera Virtual',
  [PaymentMethodType.Pix]: 'Pix',
  [PaymentMethodType.BoletoBancario]: 'Boleto',
  [PaymentMethodType.CartaoCredito]: 'Carta de Crédito',
  [PaymentMethodType.Otro]: 'Otro',
};

// Campos base comunes a todos los tipos de pago
export interface PaymentMethodBase {
  id: number;
  descripcion?: string;
  tipo: PaymentMethodType;
  empresa: number; // FK a Empresa
  activo: boolean;
}

// Efectivo: solo campos base, sin datos bancarios
export interface PaymentMethodEfectivo extends PaymentMethodBase {
  tipo: PaymentMethodType.Efectivo;
}

// Transferencia: datos bancarios por país, usando agencia para Brasil y CBU/alias para Argentina.
export interface PaymentMethodTransferencia extends PaymentMethodBase {
  tipo: PaymentMethodType.Transferencia;
  banco?: string;
  nombre_cuenta?: string;
  numero_cuenta?: string;
  numero_de_agencia?: string;
  cbu?: string;
  alias_cbu?: string;
  cuit_cuil?: string;
}

// Tarjeta Débito: datos bancarios básicos
export interface PaymentMethodTarjetaDebito extends PaymentMethodBase {
  tipo: PaymentMethodType.TarjetaDebito;
  banco?: string;
  nombre_cuenta?: string;
  numero_cuenta?: string;
}

// Tarjeta Crédito: datos bancarios básicos
export interface PaymentMethodTarjetaCredito extends PaymentMethodBase {
  tipo: PaymentMethodType.TarjetaCredito;
  banco?: string;
  nombre_cuenta?: string;
  numero_cuenta?: string;
}

// Cheque: datos bancarios + CUIT
export interface PaymentMethodCheque extends PaymentMethodBase {
  tipo: PaymentMethodType.Cheque;
  banco?: string;
  nombre_cuenta?: string;
  numero_cuenta?: string;
  cuit_cuil?: string;
}

// Billetera Virtual: clave/alias + email de pago
export interface PaymentMethodBilleteraVirtual extends PaymentMethodBase {
  tipo: PaymentMethodType.BilleteraVirtual;
  nombre_cuenta?: string;
  clave?: string;
  email_pago?: string;
}

// Pix (Brasil): clave + email de pago
export interface PaymentMethodPix extends PaymentMethodBase {
  tipo: PaymentMethodType.Pix;
  clave?: string;
  email_pago?: string;
}

// Boleto Bancario (Brasil): no requiere datos adicionales.
export interface PaymentMethodBoletoBancario extends PaymentMethodBase {
  tipo: PaymentMethodType.BoletoBancario;
}

// Cartão de Crédito (Brasil): no requiere datos adicionales.
export interface PaymentMethodCartaoCredito extends PaymentMethodBase {
  tipo: PaymentMethodType.CartaoCredito;
}

// Otro: todos los campos opcionales disponibles
export interface PaymentMethodOtro extends PaymentMethodBase {
  tipo: PaymentMethodType.Otro;
  banco?: string;
  nombre_cuenta?: string;
  numero_cuenta?: string;
  numero_de_agencia?: string;
  clave?: string;
  cbu?: string;
  alias_cbu?: string;
  cuit_cuil?: string;
  email_pago?: string;
}

// Union discriminada por 'tipo' que agrupa todos los sub-modelos
export type PaymentMethod =
  | PaymentMethodEfectivo
  | PaymentMethodTransferencia
  | PaymentMethodTarjetaDebito
  | PaymentMethodTarjetaCredito
  | PaymentMethodCheque
  | PaymentMethodBilleteraVirtual
  | PaymentMethodPix
  | PaymentMethodBoletoBancario
  | PaymentMethodCartaoCredito
  | PaymentMethodOtro;

export type PaymentMethodField =
  | 'banco'
  | 'nombre_cuenta'
  | 'numero_cuenta'
  | 'numero_de_agencia'
  | 'clave'
  | 'cbu'
  | 'alias_cbu'
  | 'cuit_cuil'
  | 'email_pago';

/**
 * Tipos disponibles por país para evitar opciones que no corresponden
 * al alta de métodos de pago del técnico.
 */
export const getPaymentMethodTypesByCountry = (isBrasil: boolean): PaymentMethodType[] =>
  isBrasil
    ? [
        PaymentMethodType.Transferencia,
        PaymentMethodType.Pix,
        PaymentMethodType.BoletoBancario,
        PaymentMethodType.CartaoCredito,
        PaymentMethodType.Otro,
      ]
    : [
        PaymentMethodType.Efectivo,
        PaymentMethodType.Transferencia,
        PaymentMethodType.TarjetaDebito,
        PaymentMethodType.TarjetaCredito,
        PaymentMethodType.Cheque,
        PaymentMethodType.BilleteraVirtual,
        PaymentMethodType.Otro,
      ];

// Campos dinámicos usados para métodos de pago en Argentina.
const ARGENTINA_PAYMENT_METHOD_FIELDS: Record<PaymentMethodType, PaymentMethodField[]> = {
  [PaymentMethodType.Efectivo]: [],
  [PaymentMethodType.Transferencia]: [
    'banco',
    'nombre_cuenta',
    'numero_cuenta',
    'cbu',
    'alias_cbu',
    'cuit_cuil',
  ],
  [PaymentMethodType.TarjetaDebito]: ['banco', 'nombre_cuenta', 'numero_cuenta'],
  [PaymentMethodType.TarjetaCredito]: ['banco', 'nombre_cuenta', 'numero_cuenta'],
  [PaymentMethodType.Cheque]: ['banco', 'nombre_cuenta', 'numero_cuenta', 'cuit_cuil'],
  [PaymentMethodType.BilleteraVirtual]: ['nombre_cuenta', 'clave', 'email_pago'],
  [PaymentMethodType.Pix]: [],
  [PaymentMethodType.BoletoBancario]: [],
  [PaymentMethodType.CartaoCredito]: [],
  [PaymentMethodType.Otro]: [
    'banco',
    'nombre_cuenta',
    'numero_cuenta',
    'clave',
    'cbu',
    'alias_cbu',
    'cuit_cuil',
    'email_pago',
  ],
};
// Campos dinámicos usados para métodos de pago en Brasil.
const BRASIL_PAYMENT_METHOD_FIELDS: Record<PaymentMethodType, PaymentMethodField[]> = {
  [PaymentMethodType.Efectivo]: [],
  [PaymentMethodType.Transferencia]: [
    'banco',
    'nombre_cuenta',
    'numero_cuenta',
    'numero_de_agencia',
    'cuit_cuil',
  ],
  [PaymentMethodType.TarjetaDebito]: [],
  [PaymentMethodType.TarjetaCredito]: [],
  [PaymentMethodType.Cheque]: [],
  [PaymentMethodType.BilleteraVirtual]: [],
  [PaymentMethodType.Pix]: ['clave', 'email_pago'],
  [PaymentMethodType.BoletoBancario]: [],
  [PaymentMethodType.CartaoCredito]: [],
  [PaymentMethodType.Otro]: [
    'banco',
    'nombre_cuenta',
    'numero_cuenta',
    'numero_de_agencia',
    'clave',
    'cuit_cuil',
    'email_pago',
  ],
};

/**
 * Campos relevantes por tipo de pago.
 * Útil para renderizar formularios dinámicos según el tipo seleccionado.
 */
export const getPaymentMethodFieldsByType = (
  tipo: PaymentMethodType,
  isBrasil: boolean,
): PaymentMethodField[] =>
  (isBrasil ? BRASIL_PAYMENT_METHOD_FIELDS : ARGENTINA_PAYMENT_METHOD_FIELDS)[tipo] ?? [];
